import { CONDITIONS as C } from './reactionClasses';

/**
 * Elements that behave as non-metals (plus the non-metal-like metalloids B, Si, As and Te)
 * and the noble gases. Their reactions with metals live in metalProfiles.js; their
 * reactions with each other are listed below.
 */
export const NONMETAL_SYMBOLS = Object.freeze(['H', 'B', 'C', 'N', 'O', 'F', 'Si', 'P', 'S', 'Cl', 'As', 'Se', 'Br', 'Te', 'I']);
export const NOBLE_GAS_SYMBOLS = Object.freeze(['He', 'Ne', 'Ar', 'Kr', 'Xe', 'Rn', 'Og']);

// Too few atoms have ever existed to see a reaction.
export const TOO_RARE_SYMBOLS = Object.freeze([
  'At', 'Fr', 'Fm', 'Md', 'No', 'Lr', 'Rf', 'Db', 'Sg', 'Bh', 'Hs', 'Mt', 'Ds', 'Rg', 'Cn', 'Nh', 'Fl', 'Mc', 'Lv', 'Ts', 'Og',
]);

export const NONMETAL_TOXIC_SYMBOLS = Object.freeze(['As', 'Se']);
export const NONMETAL_RADIOACTIVE_SYMBOLS = Object.freeze(['Rn']);

/**
 * Pairs of non-metals / metalloids / noble gases that really react when put together
 * (with heat, sparks or light where noted). Any pair of these not listed here gives
 * "no direct reaction". Hand-written famous ones live in curatedReactions.js instead.
 */
export const NONMETAL_PAIR_REACTIONS = [
  // hydrogen
  { pair: ['H', 'Br'], product: 'HBr', name: 'Hydrogen bromide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['H', 'I'], product: 'HI', name: 'Hydrogen iodide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  {
    pair: ['H', 'S'],
    product: 'H2S',
    name: 'Hydrogen sulfide',
    reactionClass: 'covalent-heat',
    conditions: C.HEAT,
    funFact: 'Hydrogen sulfide is the gas that makes rotten eggs smell so bad!',
  },
  { pair: ['H', 'Se'], product: 'H2Se', name: 'Hydrogen selenide', reactionClass: 'covalent-heat', conditions: C.HEAT },

  // carbon
  { pair: ['C', 'F'], product: 'CF4', name: 'Carbon tetrafluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['C', 'S'], product: 'CS2', name: 'Carbon disulfide', reactionClass: 'covalent-heat', conditions: C.STRONG_HEAT },
  {
    pair: ['B', 'C'],
    product: 'B4C',
    name: 'Boron carbide',
    reactionClass: 'covalent-furnace',
    conditions: C.FURNACE,
    funFact: 'Boron carbide is one of the hardest materials on Earth. It is used in armour and tank shields!',
  },

  // nitrogen
  {
    pair: ['B', 'N'],
    product: 'BN',
    name: 'Boron nitride',
    reactionClass: 'covalent-furnace',
    conditions: C.FURNACE,
    funFact: 'Boron nitride can be as slippery as pencil lead, or almost as hard as diamond!',
  },
  { pair: ['N', 'Si'], product: 'Si3N4', name: 'Silicon nitride', reactionClass: 'covalent-furnace', conditions: C.FURNACE },

  // oxygen
  {
    pair: ['B', 'O'],
    product: 'B2O3',
    name: 'Boron trioxide',
    reactionClass: 'covalent-burn',
    conditions: C.STRONG_HEAT,
    funFact: 'Boron burns with a bright green flame!',
  },
  {
    pair: ['O', 'Si'],
    product: 'SiO2',
    name: 'Silicon dioxide (quartz)',
    reactionClass: 'covalent-burn',
    conditions: C.FURNACE,
    funFact: 'Sand and quartz crystals are mostly silicon dioxide!',
  },
  { pair: ['O', 'Se'], product: 'SeO2', name: 'Selenium dioxide', reactionClass: 'covalent-burn', conditions: C.HEAT },
  { pair: ['O', 'Te'], product: 'TeO2', name: 'Tellurium dioxide', reactionClass: 'covalent-burn', conditions: C.HEAT },
  { pair: ['As', 'O'], product: 'As2O3', name: 'Arsenic trioxide', reactionClass: 'covalent-burn', conditions: C.HEAT },

  // fluorine
  { pair: ['F', 'Se'], product: 'SeF6', name: 'Selenium hexafluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['F', 'Te'], product: 'TeF6', name: 'Tellurium hexafluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['F', 'P'], product: 'PF5', name: 'Phosphorus pentafluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['As', 'F'], product: 'AsF5', name: 'Arsenic pentafluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['B', 'F'], product: 'BF3', name: 'Boron trifluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['F', 'Si'], product: 'SiF4', name: 'Silicon tetrafluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['Cl', 'F'], product: 'ClF3', name: 'Chlorine trifluoride', reactionClass: 'covalent-fluorine', conditions: C.HEAT },
  { pair: ['Br', 'F'], product: 'BrF3', name: 'Bromine trifluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  { pair: ['F', 'I'], product: 'IF5', name: 'Iodine pentafluoride', reactionClass: 'covalent-fluorine', conditions: C.ROOM },
  {
    pair: ['F', 'Kr'],
    product: 'KrF2',
    name: 'Krypton difluoride',
    reactionClass: 'covalent-fluorine',
    conditions: C.SPECIAL_LAB,
    funFact: 'Krypton difluoride only forms with electric sparks at about minus 200 degrees Celsius!',
  },
  { pair: ['F', 'Rn'], product: 'RnF2', name: 'Radon difluoride', reactionClass: 'covalent-fluorine', conditions: C.NUCLEAR_LAB },

  // chlorine
  { pair: ['Br', 'Cl'], product: 'BrCl', name: 'Bromine monochloride', reactionClass: 'halogen-halogen', conditions: C.ROOM },
  { pair: ['Cl', 'I'], product: 'ICl', name: 'Iodine monochloride', reactionClass: 'halogen-halogen', conditions: C.ROOM },
  { pair: ['Cl', 'S'], product: 'S2Cl2', name: 'Disulfur dichloride', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['Cl', 'Se'], product: 'SeCl4', name: 'Selenium tetrachloride', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['Cl', 'Te'], product: 'TeCl4', name: 'Tellurium tetrachloride', reactionClass: 'covalent-heat', conditions: C.HEAT },
  {
    pair: ['Cl', 'P'],
    product: 'PCl5',
    name: 'Phosphorus pentachloride',
    reactionClass: 'covalent-burn',
    conditions: C.ROOM,
    funFact: 'Phosphorus catches fire by itself in chlorine gas!',
  },
  { pair: ['As', 'Cl'], product: 'AsCl3', name: 'Arsenic trichloride', reactionClass: 'covalent-burn', conditions: C.ROOM },
  { pair: ['B', 'Cl'], product: 'BCl3', name: 'Boron trichloride', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['Cl', 'Si'], product: 'SiCl4', name: 'Silicon tetrachloride', reactionClass: 'covalent-heat', conditions: C.HEAT },

  // bromine
  { pair: ['Br', 'I'], product: 'IBr', name: 'Iodine monobromide', reactionClass: 'halogen-halogen', conditions: C.ROOM },
  { pair: ['Br', 'S'], product: 'S2Br2', name: 'Disulfur dibromide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['Br', 'Se'], product: 'SeBr4', name: 'Selenium tetrabromide', reactionClass: 'covalent-heat', conditions: C.ROOM },
  { pair: ['Br', 'Te'], product: 'TeBr4', name: 'Tellurium tetrabromide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['Br', 'P'], product: 'PBr3', name: 'Phosphorus tribromide', reactionClass: 'covalent-burn', conditions: C.ROOM },
  { pair: ['As', 'Br'], product: 'AsBr3', name: 'Arsenic tribromide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['B', 'Br'], product: 'BBr3', name: 'Boron tribromide', reactionClass: 'covalent-heat', conditions: C.STRONG_HEAT },
  { pair: ['Br', 'Si'], product: 'SiBr4', name: 'Silicon tetrabromide', reactionClass: 'covalent-heat', conditions: C.STRONG_HEAT },

  // iodine
  { pair: ['I', 'P'], product: 'PI3', name: 'Phosphorus triiodide', reactionClass: 'covalent-heat', conditions: C.ROOM },
  { pair: ['As', 'I'], product: 'AsI3', name: 'Arsenic triiodide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['I', 'Te'], product: 'TeI4', name: 'Tellurium tetraiodide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['B', 'I'], product: 'BI3', name: 'Boron triiodide', reactionClass: 'covalent-heat', conditions: C.STRONG_HEAT },
  { pair: ['I', 'Si'], product: 'SiI4', name: 'Silicon tetraiodide', reactionClass: 'covalent-heat', conditions: C.STRONG_HEAT },

  // sulfur, selenium, tellurium, phosphorus
  {
    pair: ['P', 'S'],
    product: 'P4S3',
    name: 'Tetraphosphorus trisulfide',
    reactionClass: 'covalent-heat',
    conditions: C.HEAT,
    funFact: 'This compound is in the tips of strike-anywhere matches!',
  },
  { pair: ['As', 'S'], product: 'As2S3', name: 'Arsenic trisulfide (orpiment)', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['B', 'S'], product: 'B2S3', name: 'Boron trisulfide', reactionClass: 'covalent-heat', conditions: C.STRONG_HEAT },
  { pair: ['S', 'Si'], product: 'SiS2', name: 'Silicon disulfide', reactionClass: 'covalent-heat', conditions: C.STRONG_HEAT },
  { pair: ['As', 'Se'], product: 'As2Se3', name: 'Arsenic triselenide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['P', 'Se'], product: 'P4Se3', name: 'Tetraphosphorus triselenide', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['As', 'Te'], product: 'As2Te3', name: 'Arsenic telluride', reactionClass: 'covalent-heat', conditions: C.HEAT },
  { pair: ['B', 'P'], product: 'BP', name: 'Boron phosphide', reactionClass: 'covalent-furnace', conditions: C.FURNACE },

  // metalloids that simply blend
  { pair: ['Ge', 'Si'], alloyName: 'Silicon-germanium alloy', reactionClass: 'alloy', conditions: C.MELT },
  { pair: ['As', 'Sb'], alloyName: 'Arsenic-antimony alloy', reactionClass: 'alloy', conditions: C.MELT },
];
